<template>
  <div class="ce-form">
    <div class="field">
      <label>Customer UTR</label>
      <input
        v-model="formData.customerUtr"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="22"
        class="input mono"
        placeholder="Enter Customer UTR (digits only)"
        @input="filterUtrInput"
      />
    </div>

    <div class="field">
      <label>Amount</label>
      <div class="input-wrap">
        <span class="input-prefix">₹</span>
        <input
          v-model.number="formData.amount"
          type="number"
          step="0.01"
          class="input has-prefix"
          placeholder="Enter Amount"
        />
      </div>
    </div>

    <p v-if="error" class="form-alert">{{ error }}</p>

    <div class="form-actions">
      <button type="button" class="btn btn-secondary" @click="$emit('close')" :disabled="isSubmitting">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="isSubmitting || !hasChanges"
        @click="submitUpdate"
      >
        <span v-if="!isSubmitting">Update order</span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Updating…
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "ClientEditOrderForm",
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  emits: ["close", "updated"],
  data() {
    return {
      formData: {
        customerUtr: this.item.customerUtr || "",
        amount: this.item.amount || null
      },
      isSubmitting: false,
      error: null
    };
  },
  computed: {
    hasChanges() {
      return (
        this.formData.customerUtr !== (this.item.customerUtr || "") ||
        this.formData.amount !== this.item.amount
      );
    }
  },
  methods: {
    // Strip non-digit characters as the user types / pastes. Mirrors
    // the customer-side payment-page filter so client / vendor edits
    // can't introduce a Unicode lookalike or space-padded UTR.
    filterUtrInput(event) {
      const raw = event.target.value;
      const cleaned = raw.replace(/[^0-9]/g, '');
      if (cleaned !== raw) {
        event.target.value = cleaned;
        this.formData.customerUtr = cleaned;
      }
    },
    async submitUpdate() {
      if (!this.hasChanges) return;

      this.isSubmitting = true;
      this.error = null;

      try {
        const payload = {};

        // Belt-and-suspenders re-strip in case anything bypassed @input.
        const cleanedUtr = String(this.formData.customerUtr || '').replace(/[^0-9]/g, '');
        if (cleanedUtr !== this.formData.customerUtr) {
          this.formData.customerUtr = cleanedUtr;
        }

        if (this.formData.customerUtr !== (this.item.customerUtr || "")) {
          payload.customerUtr = this.formData.customerUtr;
        }

        if (this.formData.amount !== this.item.amount) {
          payload.amount = this.formData.amount;
        }

        const response = await http.post(`/orders/${this.item.refID}/client-update`, payload);

        if (response.data.status) {
          this.$emit("updated", response.data.message);
          this.$emit("close");
        } else {
          this.error = response.data.message || "Update failed";
        }
      } catch (error) {
        this.error = error.response?.data?.message || "An error occurred while updating the order";
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.ce-form { display: flex; flex-direction: column; gap: 14px; }

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.input-wrap { position: relative; display: flex; align-items: center; }
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input.has-prefix { padding-left: 26px; }
.input.mono { font-family: "SFMono-Regular", Menlo, monospace; letter-spacing: 0.02em; }
.input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  margin: 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(27, 42, 71, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
